import { ApiListResponse } from "./api-helper";
import { FeatureFlagType } from "./featureFlags";

/**
 * @summary ISO-3166-2 Alpha 3
 */
export type Iso3Country =
  | "GBR"
  | "USA"
  | "AUS"
  | "NOR"
  | "NZL"
  | "ESP"
  | "GIB"
  | "CAN"
  | "IRL"
  | "CYP"
  | "FRA"
  | "NLD";
/**
 * @summary ISO-3166-2 Alpha 2
 */
export type Iso2Country =
  | "GB"
  | "US"
  | "AU"
  | "NO"
  | "NZ"
  | "ES"
  | "GI"
  | "CA"
  | "IE"
  | "CY"
  | "FR"
  | "NL";

export type Currency = "GBP" | "USD" | "AUD" | "NZD" | "NOK" | "CAD" | "EUR";

export type KisiStatus =
  | "ENABLED"
  | "DISABLED"
  | "PROCESSING_ENABLE"
  | "PROCESSING_DISABLE"
  | "PROCESSING_MODIFY";

export interface ClubBean {
  readonly id: number;
  readonly createdDate: string;
  readonly closeDate: string;
  readonly name: string;
  readonly postCode: string;
  readonly addressLine1: string;
  readonly addressLine2?: string;
  readonly city: string;
  readonly defaultCurrency: Currency;
  readonly defaultTaxRate: number;
  readonly company: CompanyDTO;
  readonly stripeApplicationId?: string;
  readonly stripeAccountType:
    | "CONNECTED"
    | "NOT_CONNECTED"
    | "STANDARD"
    | "READY_TO_CONNECT";
  readonly timezone: string;
  readonly termsConditions: string;
  readonly termsConditionsFilename: string;
  readonly email: string;
  readonly zoneId: string;
  readonly kisiStatus: KisiStatus;
  readonly kisiPlaceId?: string;
  readonly customPaymentsEnabled: boolean;
  readonly googleTagContainerId: string;
  readonly directDebitEnabled: boolean;
  readonly smsEnabled: boolean;
  readonly codeName: string;
}

export interface ClubInfo {
  readonly id: number;
  readonly name: string;
  readonly termsConditions: string;
}

export interface LeadHistoryDTO extends TimelineDTO {
  readonly leadStatus: LeadStatusDTO;
}

export interface LeadStatusDTO {
  readonly id: number;
  readonly name: string;
  readonly presetType: PresetLeadStatusType;
  readonly statusOrder: number;
  readonly deletable: boolean;
  readonly deleted: boolean;
  readonly editableOrder: boolean;
}

export type NoteDTO = {
  readonly id: number;
  readonly nodeType: "NOTE";
  readonly createdDate: string;
  readonly content: string;
  readonly attachment?: string;
  readonly attachmentFilename?: string;
  readonly updatedDate?: string;
  readonly userMemberId?: string;
  readonly createdBy: string;
  readonly createdByFirstName: string;
  readonly createdByLastName: string;
  readonly leadId?: string;
  readonly appointmentId?: number;
};

export interface PromotionRestrictedItem {
  readonly id: number;
  readonly name: string;
}

export type PromotionLimitCategory =
  | "PRODUCT"
  | "MEMBERSHIP"
  | "SESSION_PACK"
  | "APPOINTABLE";

export interface PromotionDTO {
  readonly id: number;
  readonly name: string;
  readonly code: string;
  readonly upfrontDiscount: boolean;
  readonly upfrontDiscountProrata?: boolean;
  readonly upfrontDiscountAmount?: number;
  readonly upfrontDiscountType?: DiscountType;
  readonly recurringDiscount: boolean;
  readonly recurringDiscountAmount?: number;
  readonly recurringDiscountType?: DiscountType;
  readonly recurringDiscountDuration?: number;
  readonly limitProductId?: number;
  readonly limitMembershipId?: number;
  readonly limitSessionPackId?: number;
  readonly limitUsagesPerMember?: number;
  readonly limitUsages?: number;
  readonly trial: boolean;
  readonly active: boolean;
  readonly expiry?: string;
  readonly timesUsed: number;
  readonly limitProductList?: PromotionRestrictedItem[];
  readonly limitMembershipList?: PromotionRestrictedItem[];
  readonly limitSessionPackList?: PromotionRestrictedItem[];
  readonly limitAppointableList?: PromotionRestrictedItem[];
  readonly limitProductIdList?: number[];
  readonly limitMembershipIdList?: number[];
  readonly limitSessionPackIdList?: number[];
  readonly limitAppointableIdList?: number[];
  readonly limitCategories?: PromotionLimitCategory[];
}

export interface PromotionPostDTO
  extends Omit<
    PromotionDTO,
    | "id"
    | "timesUsed"
    | "limitProductIdList"
    | "limitMembershipIdList"
    | "limitSessionPackIdList"
    | "limitAppointableIdList"
  > {
  readonly limitProductIdList: number[];
  readonly limitMembershipIdList: number[];
  readonly limitSessionPackIdList: number[];
  readonly limitAppointableIdList: number[];
}

export interface MembershipBean {
  readonly id: number;
  readonly createdDate: string;
  readonly updatedDate: string;
  readonly endDate: string;
  readonly name: string;
  readonly description: string;
  readonly type: MembershipType;
  readonly durationType: MembershipDurationType;
  readonly duration: number;
  readonly currency: string;
  readonly defaultPrice: number;
  readonly taxRate: number;
  readonly status: ActiveInactiveStatus;
  readonly termsConditions: string;
  readonly termsConditionsFilename: string;
  readonly billingType?: BillingType;
  readonly monthlyBillingDay?: number;
  readonly weeklyBillingDay?: DayOfWeek;
  readonly billingPeriod: number;
  readonly calculateProrata: ProrataCalculation;
  readonly membershipAddonList?: MembershipAddonDTO[];
  readonly proRataPayment: number;
  readonly addonsProRataPayment: number;
  readonly membershipProRataPayment: number;
  readonly recurringAddonsProRataPayment: number;
  readonly prepaidMembershipPayment: number;
  readonly totalPayableToday: number;
  readonly totalAmount: number;
  readonly nextMonthlyPayment: number;
  readonly addonsProratedList?: MembershipAddonPurchaseDTO[];
  readonly isPublic: boolean;
  readonly isTrial: boolean;
  readonly fixedStartDate?: string;
  readonly fixedEndDate?: string;
  readonly overduePaymentFeeGraceDays: number;
  readonly overduePaymentFeeAmount?: number;
}

export interface UserMemberBean extends Searchable {
  readonly id: string;
  readonly createdDate: string;
  readonly updatedDate: string;
  readonly mobileNumber: string;
  readonly dateBirth: string;
  readonly postCode: string;
  readonly addressLine1: string;
  readonly addressLine2?: string;
  readonly city: string;
  readonly gender: Gender;
  readonly country: Iso3Country;
  readonly emergencyContactName: string;
  readonly emergencyContact: string;
  readonly emailCommunication: boolean;
  readonly smsCommunication: boolean;
  readonly pushCommunication: boolean;
  readonly picture?: string;
  readonly currentBalance: number;
  readonly balanceDate: string;
  readonly isFullUser: boolean;
  readonly subscriptions: UserMemberSubscriptionBean[];
  readonly eventsAttended: number;
  readonly lead: LeadDTO;
  readonly userMember: UserType;
  readonly kisiLoginId?: string;
  readonly kisiUserId?: string;
  readonly isClubWaiverAccepted: boolean;
  readonly clubWaiverAcceptanceDate: string;
  readonly lastKisiPrimaryDeviceChangeDate?: string;
  readonly assignedStaffMembers: UserStaffBean[];
  readonly homeClubId: number;
  readonly personalNumber: string;
}

export interface UserMemberPost {
  readonly email: string;
  readonly password?: string;
  readonly firstName: string;
  readonly lastName: string;
  readonly mobileNumber?: string;
  readonly dateBirth?: string;
  readonly postCode?: string;
  readonly addressLine1?: string;
  readonly addressLine2?: string;
  readonly city?: string;
  readonly gender?: Gender;
  readonly country: Iso3Country;
  readonly emergencyContactName?: string;
  readonly emergencyContact?: string;
  readonly emailCommunication: boolean;
  readonly smsCommunication: boolean;
  readonly pushCommunication: boolean;
  readonly isClubWaiverAccepted: boolean;
  readonly sourceId: number;
}

export interface UserMemberCreated {
  readonly id: string;
  readonly email: string;
  readonly firstName: string;
  readonly lastName: string;
  readonly createdDate: string;
  readonly updatedDate: string;
  readonly mobileNumber?: string;
  readonly dateBirth?: string;
  readonly postCode?: string;
  readonly addressLine1?: string;
  readonly addressLine2?: string;
  readonly city?: string;
  readonly gender?: Gender;
  readonly country: string;
  readonly emergencyContactName?: string;
  readonly emergencyContact?: string;
  readonly emailCommunication: boolean;
  readonly smsCommunication: boolean;
  readonly pushCommunication: boolean;
  readonly isFullUser: boolean;
  readonly lead: LeadDTO;
  readonly userMember: UserType;
  readonly kisiLoginId?: string;
  readonly isClubWaiverAccepted: boolean;
  readonly clubWaiverAcceptanceDate: string;
}

export interface UserMemberSearchByFullNameResult {
  readonly id: string;
  readonly email: string;
  readonly createdDate: string;
  readonly updatedDate: string;
  readonly firstName: string;
  readonly lastName: string;
  readonly userType: UserType;
  readonly profileType: UserProfileType;
  readonly eventsAttended: number;
  readonly kisiUserId: string;
  readonly kisiLoginId: string;
  readonly isClubWaiverAccepted: boolean;
}

export interface UserMemberRsvp {
  readonly id: string;
  readonly firstName: string;
  readonly lastName: string;
  readonly email?: string;
  readonly userType: UserType;
  readonly profileType: UserProfileType;
  readonly createdDate: string;
  readonly updatedDate: string;
  readonly mobileNumber?: string;
  readonly dateBirth?: string;
  readonly postCode?: string;
  readonly addressLine1?: string;
  readonly addressLine2?: string;
  readonly city?: string;
  readonly gender?: Gender;
  readonly country: string;
  readonly emergencyContactName?: string;
  readonly emergencyContact?: string;
  readonly emailCommunication: boolean;
  readonly smsCommunication: boolean;
  readonly pushCommunication: boolean;
  readonly picture?: string;
  readonly isFullUser: boolean;
  readonly subscriptions: UserMemberSubscriptionBean[];
  readonly eventsAttended: number;
  readonly lead: LeadFieldDTO;
  readonly kisiLoginId: string;
  readonly isClubWaiverAccepted: boolean;
  readonly clubWaiverAcceptanceDate: string;
}
export interface UserMemberPurchaseDTO {
  readonly userMember: UserMemberBean;
  readonly invoice: InvoiceDTO;
}

export type UserMemberSubscriptionBean = {
  readonly id: number;
  readonly membershipBean?: MembershipBean;
  readonly sessionPackBean?: SessionPackDTO;
  readonly productBean?: ProductDTO;
  readonly sessionsLeft?: number;
  readonly startDate: string;
  readonly endDate: string;
  readonly endContractDate?: string;
  readonly cancellationDate?: string;
  readonly pauseStartDate?: string;
  readonly pauseEndDate?: string;
  readonly cycle: number;
  readonly status: UserMemberSubscriptionStatus;
  readonly active: boolean;
  readonly name: string;
  readonly price: number;
  readonly customerCancellable: boolean;
  readonly scheduledChangeDate?: string;
  readonly scheduledChangeToMembershipName?: string;
  readonly staffCancellable: boolean;
  readonly addon: boolean;
  readonly mainSubscription: boolean;
  readonly sessionsUnlimited?: boolean;
  // @deprecated
  readonly membershipMonthlyPayment: number;
  readonly createdDate: string;
  readonly updatedDate: string;
  readonly billingType?: BillingType;
  readonly monthlyBillingDay?: number;
  readonly weeklyBillingDay?: DayOfWeek;
  readonly billingPeriod?: number;
  readonly primarySubscriptionId: number;
} & (
  | {}
  | ({
      readonly promotionCode: string;
    } & (
      | {
          readonly promotionIsExpired: false;
          readonly discountedPaymentsUnlimited: boolean;
          readonly discountedAmount: number;
          readonly discountedPaymentsLeft: number;
        }
      | {
          readonly promotionIsExpired: true;
        }
    ))
);

export type UserMemberSubscriptionBeanWithMembership =
  UserMemberSubscriptionBean & {
    readonly membershipBean: MembershipBean;
  };

export interface UserStaffBean {
  readonly id: string;
  readonly email: string;
  readonly firstName: string;
  readonly lastName: string;
  readonly mobileNumber: string;
  readonly picture?: string;
  readonly activeUser: boolean;
  readonly createdDate: string;
  readonly updatedDate: string;
  readonly homeClubId: number;
  readonly accessibleClubIds: number[];
  readonly role: StaffUserRole;
}

export type ActivityCategoryStatus = "ACTIVE" | "DISABLED";

export interface ActivityCategoryDTO {
  readonly id: number;
  readonly name: string;
  readonly color: string;
  readonly status: ActivityCategoryStatus;
}

export interface ActivityCategoryPostDTO {
  readonly name: string;
  readonly color: string;
}

export type ActivityCategoryPatchDTO = Partial<ActivityCategoryPostDTO>;

export interface ActivityDTO {
  readonly id: number;
  readonly name: string;
  readonly description: string;
  readonly activityCategoryId: number;
  readonly activityCategory: ActivityCategoryDTO;
  readonly clubId: number;
  readonly status: ActivityStatus;
  readonly ruleGroup: RuleGroup;
}

export interface ActivityCreateDTO {
  readonly activityCategoryId: number;
  readonly clubId: number;
  readonly description: string;
  readonly name: string;
}

export interface CompanyDTO {
  readonly id: number;
  readonly companyNumber: string;
  readonly vatNumber: string;
  readonly name: string;
  readonly postCode: string;
  readonly addressLine1: string;
  readonly addressLine2?: string;
  readonly city: string;
  readonly timezone: string;
}

export interface SessionPackDTO {
  readonly id: number;
  readonly name: string;
  readonly description: string;
  readonly status: ActiveInactiveStatus;
  readonly purchasePrice: number;
  readonly price: number;
  readonly membersOnlyPrice: PriceDTO;
  readonly taxRate: number;
  readonly expiryType: ExpiryType;
  readonly expiryValue: number;
  readonly sessionsIncluded: number;
  readonly sessionsUnlimited: boolean;
  readonly termsConditions: string;
  readonly termsConditionsFilename: string;
  readonly termsConditionsFile: MultipartFile;
  readonly clubIdList: number[];
  readonly activityCategoryIdList: number[];
  readonly appointableCategoryIdList: number[];
  readonly isPublic: boolean;
}

export interface SessionPackSummaryDTO {
  readonly sessionPackId: number;
  readonly upfrontPriceDetails: PromotionPriceDetailDTO;
  readonly promotionCode: string;
  readonly promotionUpfrontAmount: number;
  readonly promotionUpfrontDiscountType: DiscountType;
  readonly validPromotionCode: ValidPromotionCode;
}

export interface EmailSendingHistoryDTO extends TimelineDTO {
  readonly body: string;
  readonly subject: string;
  readonly toEmail: string;
  readonly sendTimestamp: string;
}

export interface EmailSendingResult {
  readonly sentEmails: number;
  readonly sentAttempts: number;
  readonly failedEmails: string[];
  readonly failedAttemptsWithNoEmail: [
    { readonly firstName: string; readonly lastName: string },
  ];
}

export interface EventDTO {
  readonly id: number;
  readonly facilityId: number;
  readonly facility: FacilityDTO;
  readonly activityId: number;
  readonly activity: ActivityDTO;
  readonly clubId: number;
  readonly userEventHostId: string;
  readonly userEventHost: UserStaffBean;
  readonly description: string;
  readonly status: EventStatus;
  readonly isPublic: boolean;
  readonly isBookable: boolean;
  readonly sessionCost: number;
  readonly capacity: number;
  readonly waitListCapacity: number;
  readonly isFullDayEvent: boolean;
  readonly startDate: string;
  readonly endDate: string;
  readonly startTime: string;
  readonly endTime: string;
  readonly isRecurring: boolean;
  readonly startRecurringDate: string;
  readonly endRecurringDate: string;
  readonly recurrenceType: EventRecurrenceType;
  readonly recurrencePeriod: number;
  readonly weekDays: DayOfWeek[];
  readonly createdDate: string;
  readonly updatedDate: string;
}

export interface EventOccurrenceDTO {
  readonly id: number;
  readonly event: EventDTO;
  readonly startDate: string;
  readonly endDate: string;
  readonly status: EventOccurrenceStatus;
  readonly bookedCount: number;
  readonly waitingCount: number;
  readonly isAll: boolean;
  readonly eventRsvpList: EventRsvpDTO[];
  readonly waitlistPosition?: number;
}
export type EventOccurrenceV2DTO = {
  occurrenceId: number;
  startAt: string;
  endAt: string;
  bookedCount: number;
  waitingCount: number;
  waitlistPosition: number;
  bookable: boolean;
  sessionCost: number;
  capacity: number;
  waitlistCapacity: number;
  activityId: number;
  activityName: string;
  activityDescription: string;
  activityCategoryId: number;
  facilityName: string;
  facilityPicture: string;
  hostFirstName: string;
  hostLastName: string;
  hostPicture: string;
  rsvpId: number;
  rsvpStatus: EventRsvpStatus;
};

export interface EventOccurrenceListItemDTO {
  readonly id: number;
  readonly event: EventDTO;
  readonly startDate: string;
  readonly endDate: string;
  readonly status: EventOccurrenceStatus;
  readonly bookedCount: number;
  readonly waitingCount: number;
  readonly isAll: boolean;
}

export interface EventRsvpDTO {
  readonly id: number;
  readonly userMember: UserMemberRsvp;
  readonly status: EventRsvpStatus;
}

export interface EventRsvpHistoryDTO extends TimelineDTO {
  readonly activityName: string;
  readonly eventDescription: string;
  readonly eventStartDate: string;
  readonly eventEndDate: string;
  readonly status: EventRsvpStatus;
  readonly eventHost: UserStaffBean;
}

export interface CalendarEventOccurrence {
  readonly eventOccurrenceId: number;
  readonly startDate: string;
  readonly endDate: string;
  readonly activityName: string;
  readonly activityDescription: string;
  readonly activityId: number;
  readonly hostName: string;
  readonly hostStaffId: string;
  readonly facilityName: string;
  readonly facilityId: number;
  readonly sessionCost: number;
  readonly capacity?: number;
  readonly waitListCapacity?: number;
  readonly bookedCount?: number;
  readonly waitingCount?: number;
  readonly bookable: boolean;
}

export interface CalendarEventOccurrenceAsMember
  extends CalendarEventOccurrence {
  readonly eventRsvpId?: number;
  readonly eventRsvpStatus?: EventRsvpStatus;
  readonly hostPicture?: string;
  readonly waitlistQueuePosition?: number;
}

export interface BookingAgendaResult {
  readonly eventRsvpId: number;
  readonly eventOccurrenceId: number;
  readonly startDate: string;
  readonly endDate: string;
  readonly bookingName: string;
  readonly bookingDescription: string;
  readonly bookable: boolean;
  readonly facilityName: string;
  readonly facilityId: number;
  readonly hostName: string;
  readonly hostPicture?: string;
  readonly hostStaffId: string;
  readonly eventRsvpStatus: EventRsvpStatus;
  readonly sessionCost: number;
  readonly capacity: number;
  readonly waitListCapacity: number;
  readonly bookedCount?: number;
  readonly waitingCount?: number;
}

export interface CalendarAppointment {
  readonly appointmentId: number;
  readonly recurringAppointmentId?: number;
  readonly startDate: string;
  readonly endDate: string;
  readonly appointableName: string;
  readonly participantName: string;
  readonly participantMemberId: string;
  readonly hostName?: string;
  readonly hostStaffId?: string;
  readonly facilityName?: string;
  readonly facilityId?: number;
  readonly facilityPicture?: string;
  readonly status: AppointmentStatus;
  readonly paymentStatus: AppointmentPaymentStatus;
}

export interface FacilityCategoryDTO {
  readonly id: number;
  readonly name: string;
  readonly picture?: string;
}

export interface FacilityDTO {
  readonly id: number;
  readonly name: string;
  readonly description: string;
  readonly facilityCategoryId: number;
  readonly clubId: number;
  readonly status: FacilityStatus;
  readonly picture?: string;
}

export interface FacilityCreateDTO {
  readonly name: string;
  readonly facilityCategoryId: number;
  readonly description?: string;
}

export interface InvoiceDTO {
  readonly number: string;
  readonly totalAmount: number;
  readonly dueAmount: number;
  readonly status: InvoiceStatus;
  readonly issueDate: string;
}

export type InvoiceNewDTO = {
  readonly id: number;
  readonly number: string;
  readonly totalAmount: number;
  readonly dueAmount: number;
  readonly status: InvoiceStatus;
  readonly issueDate: string;
  readonly lastPaymentErrorCode: string;
  readonly lastPaymentAttemptDate: string;

  readonly promotionCode: string;

  readonly paymentAttemptCount: number;
  readonly paymentAutoRetryCount: number;
  readonly paymentAutoRetryLimit: number;
  readonly nextAutoRetryDate: string;
} & (
  | {
      readonly subscriptionId: number;
      readonly subscriptionName: string;
      readonly subscriptionPrice: number;
      readonly membershipId: number;
    }
  | {
      readonly subscriptionId: number;
      readonly subscriptionName: string;
      readonly subscriptionPrice: number;
      readonly sessionPackId: number;
    }
  | {
      readonly appointmentId: number;
      readonly appointableId: number;
      readonly appointableName: string;
      readonly appointmentPrice: number;
    }
  | {
      readonly productOrderId: number;
      readonly productOrderAmount: number;
      readonly productOrderItems: {
        readonly productId: number;
        readonly productName: string;
        readonly productPrice: number;
        readonly quantity: number;
        readonly amount: number;
        readonly taxRate: number;
      }[];
    }
  | {
      readonly feeId: number;
      readonly feeReason: "LATE_CANCELLATION" | "NO_SHOW";
    }
);

export interface InvoiceProductOrderItemDTO {
  readonly productId: number;
  readonly productName: string;
  readonly productPrice: number;

  readonly quantity: number;
  readonly amount: number;
  readonly taxRate: number;
}

export interface InvoiceReceiptItemDTO {
  readonly name: string;
  readonly quantity: number;
  readonly priceIncTax: number;
  readonly priceExTax: number;
  readonly taxRate: number;
}

export type InvoiceReceiptTransactionType =
  | "DEBIT"
  | "REFUND_DEBIT"
  | "DISPUTE_DEBIT"
  | "PAYMENT_FEE"
  | "DISPUTE_FEE"
  | "DISPUTE_COUNTER_FEE"
  | "CREDIT"
  | "MANUAL_CREDIT"
  | "WALLET_CREDIT"
  | "WRITE_OFF"
  | "PENDING_AUTHORIZATION";

export interface InvoiceReceiptTransactionDTO {
  readonly id: number;
  readonly transactionDate: string;
  readonly type: InvoiceReceiptTransactionType;
  readonly amount: number;
  readonly customPaymentCategoryName?: string;
  readonly paymentMethodType?: PaymentMethodType;
  readonly cardWalletType?: PaymentMethodWalletType;
  readonly last4Digits?: string;
  readonly cardBrand?: CardBrandType;
  readonly linkEmail?: string;
}

export type InvoiceReceiptDTO = {
  readonly id: number;
  readonly number: string;
  readonly userMemberId: string;
  readonly totalAmount: number;
  readonly dueAmount: number;
  readonly type: InvoiceType;
  readonly status: InvoiceStatus;
  readonly issueDate: string;
  readonly items: InvoiceReceiptItemDTO[];
  readonly transactions: InvoiceReceiptTransactionDTO[];
};

export type DetailedInvoiceDTO = {
  readonly id: number;
  readonly number: string;
  readonly totalAmount: number;
  readonly dueAmount: number;
  readonly status: InvoiceStatus;
  readonly issueDate: string;
  readonly promotionId?: number;
  readonly promotionCode?: string;

  readonly lastPaymentErrorCode: string;
  readonly lastPaymentAttemptDate: string;
  readonly paymentAttemptCount: number;
  readonly paymentAutoRetryCount: number;
  readonly paymentAutoRetryLimit: number;
  readonly nextAutoRetryDate: string;
} & (
  | {
      readonly subscriptionId: number;
      readonly subscriptionName: string;
      readonly subscriptionPrice: number;
      readonly membershipId: number;
      readonly sessionPackId: number;
    }
  | {
      readonly appointmentId: number;
      readonly appointableId: number;
      readonly appointableName: string;
      readonly appointablePrice: number;
    }
  | {
      readonly productOrderId: number;
      readonly productOrderAmount: number;
      readonly productOrderItems: InvoiceProductOrderItemDTO[];
    }
  | {
      readonly feeId: number;
      readonly feeReason: "NO_SHOW" | "LATE_CANCELLATION";
    }
);

export interface InvoiceRevenueReportItemDTO {
  readonly invoiceDate: string;
  readonly userMemberId: string;
  readonly name: string;
  /** @description price after discounts */
  readonly amount: number;
  readonly subscriptionName: string;
  /** @description price before discounts */
  readonly subscriptionPrice: number;
  readonly endContractDate?: string;
  readonly promoCode?: string;
  readonly promoDiscount?: string;
  readonly promoType?: DiscountType;
  readonly cyclesLeft?: string;
  readonly recurringDiscountDuration?: string;
}

export interface InvoiceRevenueReportDTO {
  readonly total: number;
  readonly data: ApiListResponse<InvoiceRevenueReportItemDTO>;
}

export interface LeadDTO extends Searchable {
  readonly id: number;
  readonly mobileNumber: string;
  readonly source: LeadSourceDTO;
  readonly emailCommunication: boolean;
  readonly smsCommunication: boolean;
  readonly createdDate: string;
  readonly leadStatus: LeadStatusDTO;
  readonly userMember: UserMemberBean;
  readonly userMemberId?: string;
}

export interface LeadPipelineItem {
  readonly leadId: number;
  readonly createdDate: string;
  readonly firstName: string;
  readonly lastName: string;
  readonly email: string;
  readonly mobileNumber: string;
  readonly emailCommunication: boolean;
  readonly smsCommunication: boolean;
  readonly userMemberId?: string;
  readonly leadStatusId: number;
  readonly leadStatusName: string;
  readonly leadSourceId: number;
  readonly leadSourceName: string;
  readonly leadTasks?: { taskId: number; taskName: string }[];
}

export interface LeadDTOItem {
  readonly id: number;
  readonly firstName: string;
  readonly lastName: string;
  readonly mobileNumber?: string;
  readonly email: string;
  readonly source: LeadSourceDTO;
  readonly emailCommunication: boolean;
  readonly smsCommunication: boolean;
  readonly createdDate: string;
  readonly leadStatus: LeadStatusDTO;
  readonly userType: UserType;
  readonly profileType: UserProfileType;
}

export interface LeadFieldDTO {
  readonly id: number;
  readonly firstName: string;
  readonly lastName: string;
  readonly mobileNumber: string;
  readonly email: string;
  readonly source: LeadSourceDTO;
  readonly emailCommunication: boolean;
  readonly smsCommunication: boolean;
  readonly createdDate: string;
  readonly leadStatus: LeadStatusDTO;
}

export interface LeadPostDTO {
  readonly firstName: string;
  readonly lastName: string;
  readonly mobileNumber: string;
  readonly email: string;
  readonly sourceId: number;
  readonly emailCommunication: boolean;
  readonly smsCommunication: boolean;
}

export interface LeadSourceDTO {
  readonly id: number;
  readonly name: string;
  readonly activeSource: boolean;
  readonly createdDate: string;
}

export interface LeadSourceCreateDTO {
  readonly name: string;
}

export interface LeadTaskDTO {
  readonly id: string;
  readonly userMemberId: string;
  readonly email: string;
  readonly phoneNumber: string;
  readonly firstName: string;
  readonly lastName: string;
  readonly source: LeadSourceDTO;
  readonly userType: UserType;
  readonly profileType: UserProfileType;
}

export interface NoteDashboardDTO extends TimelineDTO {
  readonly content: string;
  readonly attachment: string;
  readonly attachmentFilename: string;
  readonly updatedDate: string;
  readonly userMemberId: string;
  readonly createdBy: string;
  readonly createdByFirstName: string;
  readonly createdByLastName: string;
  readonly leadId: string;
}

export type CardBrandType =
  | "amex"
  | "diners"
  | "discover"
  | "eftpos_au"
  | "jcb"
  | "mastercard"
  | "unionpay"
  | "visa"
  | "unknown";

export type PaymentMethodType =
  | "CARD"
  | "LINK"
  | "US_DIRECT_DEBIT"
  | "UK_DIRECT_DEBIT"
  | "AU_DIRECT_DEBIT"
  | "CA_DIRECT_DEBIT"
  | "EU_DIRECT_DEBIT"
  | "UNSUPPORTED";

export type PaymentMethodWalletType =
  | "LINK"
  | "GOOGLE_PAY"
  | "APPLE_PAY"
  | "UNSUPPORTED";

export interface PaymentMethodDTO {
  /*** @deprecated - use paymentMethodId instead, on the portal this is mapped from paymentMethodId */
  readonly id: string;
  readonly last4Digits?: string;
  readonly expMonth?: number;
  readonly expYear?: number;

  readonly linkEmail?: string;

  readonly createdDate: string;
  readonly defaultPaymentMethod: boolean;

  readonly type: PaymentMethodType;
  readonly cardBrand: CardBrandType;
  readonly walletType?: PaymentMethodWalletType;

  readonly status: PaymentMethodStatus;
  readonly invalidateReason?:
    | "CARD_EXPIRED"
    | "CARD_BLOCKED"
    | "CARD_NOT_ALLOWED"
    | "CARD_DETAILS_INVALID"
    | "MANDATE_INACTIVE";
  readonly paymentMethodId: string;
  readonly bankName?: string;
}
export type PaymentMethodStatus = "ACTIVE" | "PENDING" | "REMOVED" | "INVALID";

export interface ProductCategoryDTO {
  readonly id: number;
  readonly name: string;
}

export interface ProductDTO {
  readonly id: number;
  readonly name: string;
  readonly description: string;
  readonly productCategoryId: number;
  readonly currency: string;
  readonly purchasePrice: number;
  readonly price: number;
  readonly taxRate: number;
  readonly stockQuantity: number;
  readonly clubIdList: number[];
  readonly status: ProductStatus;
  readonly stockType: ProductStockType;
  readonly availableToAllClubs: boolean;
  readonly allowAppPurchases: boolean;
  readonly allowWebPurchases: boolean;
  readonly picture?: string;
  readonly membersOnlyPrice: PriceDTO;
}

export interface ProductOrderDetailDTO {
  readonly productId: number;
  readonly quantity: number;
}

export interface ProductOrderPriceDetailDTO {
  readonly productId: number;
  readonly quantity: number;
  readonly totalAmount: number;
  readonly discountedTotalAmount: number;
}

export interface ProductOrderSummaryDTO {
  readonly productOrderPriceDetails: ProductOrderPriceDetailDTO[];
  readonly upfrontPriceDetails: PromotionPriceDetailDTO;
  readonly promotionCode: string;
  readonly promotionUpfrontAmount: number;
  readonly promotionUpfrontDiscountType: DiscountType;
  readonly validPromotionCode: ValidPromotionCode;
}

export interface PromotionPriceDetailDTO {
  readonly totalAmountBeforeDiscount: number;
  readonly totalAmountToPay: number;
  readonly totalAmountDifference: number;
}

export const WindowTypeValues = ["HOURS", "DAYS", "MONTHS", "MINUTES"] as const;
export type WindowType = (typeof WindowTypeValues)[number];

export type BookingNoticeWindowType = WindowType;
export type WindowRule = { windowType: WindowType; windowValue: string };
export type SubscriptionCancellationRule = {
  readonly allowCustomerCancelMemberships: boolean;
  readonly minimumPeriodNotice: number;
};
export type SubscriptionMembershipChangeRule = {
  readonly allowCustomerChangeMemberships: boolean;
  readonly minimumPeriodNotice: number;
};
export interface RuleClubDTO {
  readonly ruleId: number;
  readonly value: string;
  readonly ruleType: RuleType;
  readonly bookingCancellationRule?: WindowRule & { cancellationFee: number };
  readonly noShowFeeRule?: { noShowFeeValue: number };
  readonly bookingRule?: WindowRule;
  readonly bookingNoticePeriodRule?: WindowRule;
  readonly checkOutRule?: { readonly windowValue: string };
  readonly subscriptionCancellationRule?: SubscriptionCancellationRule;
}

export interface RuleGroupPost {
  readonly name: string;
  readonly groupType: RuleGroupType;
  readonly ruleClubList: {
    readonly ruleType: RuleType;
    readonly bookingCancellationRule?: WindowRule & {
      cancellationFee: number;
    };
    readonly noShowFeeRule?: { noShowFeeValue: number };
    readonly bookingRule?: WindowRule;
    readonly checkOutRule?: { readonly windowValue: string };
    readonly subscriptionCancellationRule?: SubscriptionCancellationRule;
    readonly subscriptionMembershipChangeRule?: SubscriptionMembershipChangeRule;
    readonly rescheduleNoticePeriodRule?: {
      readonly windowType: BookingNoticeWindowType;
      readonly windowValue: string;
    };
    readonly bookingNoticePeriodRule?: {
      readonly windowType: BookingNoticeWindowType;
      readonly windowValue: string;
    };
    readonly bookingLimitRule?: {
      limitValue: number;
    };
    readonly concurrentBookingRule?: {
      readonly restrictConcurrentBookings: boolean;
    };
  }[];
  readonly appointableIdList?: number[];
  readonly membershipIdList?: number[];
  readonly classIdList?: number[];
}

export interface RuleGroupPatch {
  readonly name?: string;
  readonly ruleClubList: {
    readonly ruleClubId: number;
    readonly bookingCancellationRule?: WindowRule & {
      cancellationFee: number;
    };
    readonly noShowFeeRule?: { noShowFeeValue: number };
    readonly bookingRule?: WindowRule;
    readonly checkOutRule?: { readonly windowValue: string };
    readonly subscriptionCancellationRule?: SubscriptionCancellationRule;
    readonly rescheduleNoticePeriodRule?: {
      readonly windowType: BookingNoticeWindowType;
      readonly windowValue: string;
    };
    readonly bookingNoticePeriodRule?: {
      readonly windowType: BookingNoticeWindowType;
      readonly windowValue: string;
    };
    readonly concurrentBookingRule?: {
      readonly restrictConcurrentBookings: boolean;
    };
    readonly bookingLimitRule?: {
      readonly limitValue: number;
    };
  }[];
  readonly appointableIdList?: number[];
  readonly membershipIdList?: number[];
  readonly classIdList?: number[];
}

export interface FormRule {
  readonly isRequired: boolean;
}

export type RuleType =
  | "BOOKING_CANCELLATION_WINDOW"
  | "BOOKING_LIMIT_WINDOW"
  | "NO_SHOW_FEE"
  | "USER_FORM_REQUIREMENT_CONFIG"
  | "AUTOMATIC_CHECK_OUT"
  | "SUBSCRIPTION_CANCELLATION"
  | "SUBSCRIPTION_MEMBERSHIP_CHANGE"
  | "BOOKING_NOTICE_PERIOD"
  | "BOOKING_LIMIT_PER_CUSTOMER"
  | "CONCURRENT_BOOKING"
  | "RESCHEDULE_NOTICE_PERIOD";

export interface RuleDTO {
  readonly id: number;
  readonly ruleType: RuleType;
  readonly ruleName: string;
  readonly ruleDefaultValue: string;
  readonly ruleDescription: string;
}

export type RuleGroupType =
  | "APPOINTABLE_GROUP"
  | "CLASS_GROUP"
  | "MEMBERSHIP_GROUP"
  | "ACCESS_GROUP";

export interface RuleGroupItemDTO {
  readonly ruleId: number;
  readonly clubRuleId: number;
  readonly ruleType: RuleType;
  readonly bookingCancellationRule?: {
    readonly windowType: WindowType;
    readonly windowValue: string;
    readonly cancellationFee: number;
  };
  readonly bookingRule?: WindowRule;
  readonly noShowFeeRule?: {
    readonly noShowFeeValue: number;
  };
  readonly bookingLimitRule?: {
    readonly limitValue?: number;
  };
  readonly subscriptionCancellationRule?: {
    readonly allowCustomerCancelMemberships: boolean;
    readonly minimumPeriodNotice: number;
  };
  readonly subscriptionMembershipChangeRule?: {
    readonly allowCustomerChangeMemberships: boolean;
    readonly minimumPeriodNotice: number;
  };
  readonly checkOutRule?: {
    readonly windowValue: string;
  };
  readonly rescheduleNoticePeriodRule?: WindowRule;
  readonly bookingNoticePeriodRule?: WindowRule;
  readonly concurrentBookingRule?: {
    readonly restrictConcurrentBookings: boolean;
  };
}

export type RuleGroup = {
  readonly id: number;
  readonly name: string;
  readonly isDefault: boolean;
  readonly groupType: RuleGroupType;
  readonly isActive: boolean;
  readonly createdDate: string;
  readonly ruleClubList: RuleGroupItemDTO[];
  readonly appointableList?: AppointableDTO[];
  readonly membershipList?: Membership[];
  readonly classList?: { readonly id: number; readonly name: string }[];
};

export interface MembershipAddonDTO {
  readonly id: number;
  readonly sessionPackId: number;
  readonly productId: number;
  readonly name: string;
  readonly activityCategoryIdList: number[];
  readonly appointableCategoryIdList: number[];
  readonly sessionsUnlimited: boolean;
  readonly quantity: number;
  readonly recurring: boolean;
  readonly price: number;
  readonly currency: string;
  readonly credits: number;
}

export interface MembershipSummaryDTO {
  readonly membershipId: number;
  readonly recurringMembershipAndAddonsProratedAmount: number;
  readonly prepaidMembershipAmount: number;
  readonly addonsProratedList?: MembershipAddonPurchaseDTO[];
  readonly upfrontPriceDetails: PromotionPriceDetailDTO;
  readonly prorataPriceDetails: PromotionPriceDetailDTO;
  readonly recurringPriceDetails: PromotionPriceDetailDTO;
  readonly promotionCode: string;
  readonly promotionUpfrontAmount: number;
  readonly promotionUpfrontDiscountType: DiscountType;
  readonly promotionRecurringAmount: number;
  readonly promotionRecurringDiscountType: DiscountType;
  readonly promotionRecurringDiscountDuration: number;
  readonly validPromotionCode: ValidPromotionCode;
}

export interface SubscriptionPromotion {
  readonly subscriptionId: number;
  readonly promotionId: number;
  readonly promotionCode: string;
  readonly recurringPriceDetails: {
    readonly totalAmountBeforeDiscount: number;
    readonly totalAmountToPay: number;
    readonly totalAmountDifference: number;
  };
  readonly promotionRecurringAmount: number;
  readonly promotionRecurringDiscountType: DiscountType;
  readonly promotionRecurringDiscountDuration: number;
}

export interface SubscriptionPromotionSummary extends SubscriptionPromotion {
  readonly validPromotionCode: ValidPromotionCode;
}

export interface MembershipSubscriptionHistoryTimelineDTO extends TimelineDTO {
  readonly name: string;
  readonly status: MembershipSubscriptionHistoryStatus;
}

export interface TaskDTO extends TimelineDTO {
  readonly name: string;
  readonly complete: boolean;
  readonly deadlineDate: string;
  readonly deadlineTime: string;
  readonly notes: string;
  readonly taskRelatedLeads: LeadTaskDTO[];
  readonly taskRelatedUsers: UserMemberTaskDTO[];
  readonly taskOwners: UserStaffTaskDTO[];
}

export interface TaskPatchDTO {
  readonly name: string;
  readonly complete: boolean;
  readonly notes: string;
  readonly ownerIdList: string[];
  readonly relatedUserIdList: string[];
  readonly relatedLeadIdList: number[];
  readonly deadlineDate: string;
  readonly deadlineTime: string;
}

export interface TaskPostDTO {
  readonly name: string;
  readonly deadlineDate: string;
  readonly deadlineTime: string;
  readonly notes: string;
  readonly ownerIdList: string[];
  readonly relatedUserIdList: string[];
  readonly relatedLeadIdList: number[];
}

export interface TemplateDTO {
  readonly id: number;
  readonly templateName: string;
  readonly templateType: TemplateType;
  readonly subject: string;
  readonly isEditable: boolean;
  readonly isAutomationTemplate: boolean;
  readonly body: string;
  readonly templatePlaceholderType: AutomationType;
  readonly createdDate: string;
  readonly updatedDate: string;
}

export interface TimelineDTO {
  readonly id: number;
  readonly nodeType: TimelineNodeType;
  readonly createdDate: string;
}

export interface EventAttendanceDTO {
  readonly userMemberEventAttendanceDTOPage: ApiListResponse<UserMemberEventAttendanceDTO>;
  readonly attendanceTotal: number;
}

export interface UserMemberEventAttendanceDTO
  extends UserMemberEventAttendanceView {
  readonly email: string;
  readonly lastAttended: string;
}

export interface ClassesRunItemDTO {
  readonly startTime: string;
  readonly className: string;
  readonly capacity: number;
  readonly trainer: string;
  readonly occurrenceId: number;
  readonly occupancy: number;
  readonly occupancyPercentage: number;
}

export interface ClassesRunDTO {
  readonly classesRunReportDTO: ApiListResponse<ClassesRunItemDTO>;
  readonly classesRunTotal: number;
}

export interface NoShowItemDTO {
  readonly startTime: string;
  readonly className: string;
  readonly trainerFirstName: string;
  readonly trainerLastName: string;
  readonly memberFirstName: string;
  readonly memberLastName: string;
  readonly feeCollected: number;
  readonly cancellationTime: string;
}

export interface ReportDTO<T> {
  readonly items: ApiListResponse<T>;
  readonly totalCount: number;
}

export interface LateCancellationItemDTO {
  readonly startTime: string;
  readonly className: string;
  readonly trainerFirstName: string;
  readonly trainerLastName: string;
  readonly memberFirstName: string;
  readonly memberLastName: string;
  readonly cancellationTime: string;
  readonly feeCollected: number;
}

export interface MembershipSoldDTO {
  readonly firstName: string;
  readonly lastName: string;
  readonly userMemberId: string;
  readonly membershipName: string;
  readonly createdDate: string;
}

export interface MembershipPausingDTO {
  readonly subscriptionId: number;
  readonly userMemberId: string;
  readonly firstName: string;
  readonly lastName: string;
  readonly membershipName: string;
  readonly membershipType: string;
  readonly pauseDate: string;
  readonly resumeDate: string;
}

export interface MembershipResumingDTO {
  readonly subscriptionId: number;
  readonly userMemberId: string;
  readonly firstName: string;
  readonly lastName: string;
  readonly membershipName: string;
  readonly membershipType: string;
  readonly pauseDate: string;
  readonly resumeDate: string;
}

export interface MembershipSoldReportDTO {
  readonly membershipSoldDTO: ApiListResponse<MembershipSoldDTO>;
  readonly membershipSoldTotal: number;
}

export type ReportMemberDTO = {
  readonly email: string;
  readonly firstName: string;
  readonly lastName: string;
  readonly mobileNumber: string;
  readonly userMemberId: string;
  readonly cancellationDate?: string;
  readonly expiryDate?: string;
  readonly membershipType?: string;
  readonly pausedDate?: string;
  readonly resumeDate?: string;
  readonly startDate?: string;
};

export type MembershipReportDTO = ApiListResponse<ReportMemberDTO>;

export type ReportMembershipEndingLineDTO = {
  readonly subscriptionId: number;
  readonly userMemberId: string;
  readonly firstName: string;
  readonly lastName: string;
  readonly email: string;
  readonly membershipName: string;
  readonly membershipType: string;
  readonly endDate: string;
  readonly cancellationReason?: string;
  readonly submittedBy?: string;
  readonly submittedDate?: string;
};

export type MembershipEndingReportDTO =
  ApiListResponse<ReportMembershipEndingLineDTO>;

export interface LeadReportDTO {
  readonly leadReportByPresetStatusViewDTO: ApiListResponse<LeadReportItemDTO>;
  readonly leadReportTotal: number;
}

export interface OpenLeadReportDTO {
  readonly leadReportByOpenStatusViewDTO: ApiListResponse<LeadReportItemDTO>;
  readonly leadReportTotal: number;
}

export type RevenuePaymentType = "CARD" | "LINK" | "DIRECT_DEBIT" | "CUSTOM";

export type InvoiceType =
  | "VOUCHER"
  | "MEMBERSHIP"
  | "CREDIT_PACK"
  | "PRODUCT_ORDER"
  | "APPOINTMENT"
  | "FEE"
  | "LATE_PAYMENT_FEE";

export interface RevenueReportItemDTO {
  readonly item: string;
  readonly soldBy?: string;
  readonly soldTo: string;
  readonly latePayment: boolean;
  readonly paymentType: RevenuePaymentType;
  readonly paymentMethodType: PaymentMethodType;
  readonly linkEmail?: string;
  readonly last4Digits?: string;
  readonly cardBrand?: CardBrandType;
  readonly walletType?: PaymentMethodWalletType;
  readonly customPaymentCategoryName?: string;
  readonly transactionId: number;
  readonly transactionType: TransactionType;
  readonly transactionDate: string;
  readonly transactionAmount: number;
  readonly invoiceNumber: string;
  readonly invoiceType: InvoiceType;
  readonly invoiceDate: string;
  readonly userMemberId: string;
  readonly userStaffId?: string;
}

export interface RevenueReportDTO {
  readonly data: ApiListResponse<RevenueReportItemDTO>;
  readonly total: number;
}

export interface TransactionReportItemDTO {
  readonly amount: number;
  readonly invoiceDate?: string;
  readonly date?: string;
  readonly name: string;
}

export interface TransactionReportDTO {
  readonly data: ApiListResponse<TransactionReportItemDTO>;
  readonly total: number;
}

export interface UserMemberTaskDTO {
  readonly id: string;
  readonly email: string;
  readonly phoneNumber: string;
  readonly leadDTO: LeadDTO;
  readonly firstName: string;
  readonly lastName: string;
  readonly picture?: string;
  readonly profileType: UserProfileType;
  readonly userType: UserType;
  readonly source: LeadSourceDTO;
}

export interface UserStaffTaskDTO {
  readonly id: string;
  readonly firstName: string;
  readonly lastName: string;
  readonly picture?: string;
}

export interface MultipartFile extends InputStreamSource {
  readonly name: string;
  readonly bytes: any;
  readonly empty: boolean;
  readonly resource: Resource;
  readonly size: number;
  readonly contentType: string;
  readonly originalFilename: string;
}

export interface MembershipAddonPurchaseDTO {
  readonly id: number;
  readonly name: string;
  readonly amount: number;
  readonly taxRate: number;
  readonly quantity: number;
  readonly recurring: boolean;
  readonly sessionsUnlimited: boolean;
  readonly proratedSessions: number;
}

export interface Searchable {
  readonly email: string;
  readonly firstName: string;
  readonly lastName: string;
  readonly profileType: UserProfileType;
}

export interface LeadReportItemDTO {
  readonly createdDate: string;
  readonly email: string;
  readonly firstName: string;
  readonly lastName: string;
  readonly leadId: number;
  readonly mobileNumber: string;
  readonly status: string;
  readonly source: string;
  readonly userMemberId: string;
  readonly profileType: UserProfileType;
}

export interface UserMemberEventAttendanceView {
  readonly userMemberId: string;
  readonly attendance: number;
  readonly firstName: string;
  readonly lastName: string;
  readonly mobileNumber: string;
}

export interface Resource extends InputStreamSource {
  open: boolean;
  file: any;
  readable: boolean;
  url: URL;
  description: string;
  uri: {};
  filename: string;
}

export interface InputStreamSource {
  inputStream: any;
}

export interface Membership {
  readonly id: number;
  readonly name: string;
  readonly description: string;
  readonly type: MembershipType;
  readonly durationType: MembershipDurationType;
  readonly duration: number;
  readonly currency: string;
  readonly defaultPrice: number;
  readonly taxRate: number;
  readonly endDate: string;
  readonly status: ActiveInactiveStatus;
  readonly termsConditions: string;
  readonly termsConditionsFilename: string;
  readonly isPublic: boolean;
  readonly isTrial: boolean;
  readonly calculateProrata: ProrataCalculation;
  readonly createdDate: string;
  readonly updatedDate: string;
  readonly hasSubscriptions: boolean;
  readonly fixedStartDate?: string;
  readonly fixedEndDate?: string;

  readonly billingType: BillingType;
  readonly monthlyBillingDay: number;
  readonly weeklyBillingDay: DayOfWeek;
  readonly billingPeriod: number;
  readonly fixedBillingDay: boolean;
}

export interface ClubPropertiesDTO {
  readonly allowCustomerSelectingStartDate: boolean;
  readonly defaultNationality: Iso3Country;
  readonly gymOpeningDate: string;
  readonly shortDateFormat: string;
  readonly stripePublicKey: string;
  readonly stripeOnboardPublicKey: string;
  /*** ISO3 Country Code */
  readonly countryCode: string;

  /*** @deprecated */
  readonly dateFormat: string;
  /*** @deprecated */
  readonly longDateFormat: string;
  /*** @deprecated */
  readonly phoneNumberCountry: Iso2Country[];
  /*** @deprecated - use countryCode instead */
  readonly postalCodeCountry: Iso2Country;
}

export interface ClubPropertiesDTOWithRawPhoneNumberCountry
  extends Omit<ClubPropertiesDTO, "phoneNumberCountry"> {
  readonly phoneNumberCountry: string;
}

export interface ChangedEventRsvp {
  readonly id: number;
  readonly userMember: UserMemberBean;
  readonly status: EventRsvpStatus;
}

export interface ClubVoucherSettingsDTO {
  voucherSalesEnabled: boolean;
}

export interface ClubWebSettings {
  readonly accentColor: string;
  readonly backgroundColor: string;
  readonly cardColor: string;
  readonly font: string;
  readonly fontColor: string;
  readonly footerColor: string;
  readonly logo: string;
  readonly navbarColor: string;
  readonly primaryColor: string;
  readonly shadowColor: string;
  // New Hosted pages fields
  readonly darkMode: boolean;
  readonly membershipsNavEnabled: boolean;
  readonly creditPacksNavEnabled: boolean;
  readonly productNavEnabled: boolean;
  readonly appointmentsNavEnabled: boolean;
  readonly timetableNavEnabled: boolean;
  readonly leadCaptureNavEnabled: boolean;
  readonly yourWebsiteUrl: string;
  readonly redirectUrl: string;
  readonly grayShades: {
    [key in
      | "0"
      | "25"
      | "50"
      | "100"
      | "200"
      | "300"
      | "400"
      | "500"
      | "600"
      | "700"
      | "800"
      | "900"
      | "950"]: string;
  };
}

export type KisiLock = {
  readonly id: string;
  readonly name: string;
  readonly description: string;
  readonly latitude: number;
  readonly longitude: number;
  readonly configured: boolean;
  readonly online: boolean;
  readonly unlocked: boolean;
  readonly lockedDown: boolean;
  readonly open: boolean;
  readonly placeId: string;
};

export type TimelineNodeType =
  | "NOTE"
  | "RSVP"
  | "EMAIL"
  | "LEADSTATUS"
  | "SUBSCRIPTION"
  | "TASK";

export type PresetLeadStatusType =
  | "NEW_LEAD"
  | "DEAL_CLOSED"
  | "DEAL_LOST"
  | "TRIAL";

export type DiscountType = "CURRENCY" | "PERCENTAGE";

export type MembershipType = "RECURRING" | "PREPAID";

export type MembershipDurationType =
  | "DAILY"
  | "WEEKLY"
  | "MONTHLY"
  | "NO_CONTRACT";

export type ActiveInactiveStatus = "ACTIVE" | "INACTIVE";

export type BillingType = "WEEKLY" | "MONTHLY";

export type DayOfWeek =
  | "MONDAY"
  | "TUESDAY"
  | "WEDNESDAY"
  | "THURSDAY"
  | "FRIDAY"
  | "SATURDAY"
  | "SUNDAY";

export type ProrataCalculation = "YES" | "NO" | "WAIVE";

export type Gender = "MALE" | "FEMALE" | "PREFER_NOT_TO_SAY";

export type UserType = "LEAD" | "CUSTOMER" | "MEMBER" | "DELETED";

export type UserProfileType = "USER" | "LEAD";

export type UserMemberSubscriptionStatus =
  | "ACTIVE"
  | "PENDING"
  | "PAUSED"
  | "CANCELLED"
  | "EXPIRED"
  | "DELETED"
  | "OVERDUE";

export type UserMemberSubscriptionHistoryStatus =
  | "ACTIVE"
  | "PENDING"
  | "PAUSED"
  | "CANCELLED"
  | "EXPIRED"
  | "DELETED"
  | "OVERDUE"
  | "UPGRADED"
  | "DOWNGRADED"
  | "CHANGED";

export type ActivityStatus = "ACTIVE" | "DISABLED";

export type ExpiryType = "WEEKLY" | "MONTHLY" | "NA";

export type ValidPromotionCode = "YES" | "NO" | "NOT_FOR_THIS_PURCHASE";

export type EventStatus = "CREATED" | "CANCELLED";

export type EventRecurrenceType = "DAILY" | "WEEKLY" | "MONTHLY" | "YEARLY";

export type EventOccurrenceStatus =
  | "CREATED"
  | "IN_PROGRESS"
  | "FINISHED"
  | "CANCELLED";

export type EventRsvpStatus =
  | "WAITING"
  | "BOOKED"
  | "ATTENDED"
  | "CANCELLED"
  | "LATE_CANCELLED"
  | "NO_SHOW";

export type FacilityStatus = "ACTIVE" | "DISABLED";

export type InvoiceStatus =
  | "PAID"
  | "SCHEDULED"
  | "RE_SCHEDULE"
  | "PAST_DUE"
  | "VOID"
  | "REFUNDED"
  | "PROCESSING"
  | "DISPUTED"
  | "WRITTEN_OFF"
  | "AWAITING_AUTHORIZATION";

export type ProductStatus = "AVAILABLE" | "UNAVAILABLE";

export type ProductStockType = "LIMITED" | "UNLIMITED";

export type MembershipSubscriptionHistoryStatus =
  | "ACTIVE"
  | "PENDING"
  | "PAUSED"
  | "CANCELLED"
  | "EXPIRED"
  | "DELETED"
  | "OVERDUE";

export type TemplateType = "SMS" | "EMAIL";

export type AutomationType =
  | "MEMBERSHIP"
  | "EVENT"
  | "PAYMENT"
  | "GENERIC"
  | "LEAD"
  | "FAIL_PAYMENT"
  | "STRONG_CUSTOMER_AUTHENTICATION"
  | "ACCOUNT_DELETE";

export type AutomationPlaceholder =
  | "activityName"
  | "amount"
  | "authorizationUrl"
  | "cancellationDate"
  | "clubAddressLine1"
  | "clubAddressLine2"
  | "clubCity"
  | "clubEmail"
  | "clubName"
  | "clubPostCode"
  | "domainUrl"
  | "eventOccurrenceEndDate"
  | "eventOccurrenceStartDate"
  | "expMonth"
  | "expYear"
  | "facilityName"
  | "last4Digits"
  | "pauseEndDate"
  | "pauseStartDate"
  | "purchaseDate"
  | "subscriptionName"
  | "userFirstName"
  | "userLastName"
  | "userStaffFirstName"
  | "userStaffLastName";

export type AutomationPlaceholderLabels = {
  [key in AutomationPlaceholder]: string;
};

export type TransactionType =
  | "DEBIT"
  | "CREDIT"
  | "MANUAL_CREDIT"
  | "REFUND_DEBIT"
  | "WRITE_OFF"
  | "PENDING_AUTHORIZATION";

export interface KisiPlace {
  readonly id: string;
  readonly name: string;
  readonly address: string;
}

export interface KisiGroup {
  readonly id: string;
  readonly name: string;
}

export type KisiGroupMappingType = "MEMBERSHIP" | "FACILITY";

export interface KisiGroupMapping {
  readonly id: number;
  readonly name: string;
  readonly description: string;
  readonly kisiGroupId: string;
  readonly kisiGroupName: string;
  readonly type: KisiGroupMappingType;
}

export type AccessReportItemDTO = {
  readonly id: number;
  // User Member
  readonly userMemberId: string;
  readonly firstName: string;
  readonly lastName: string;
  readonly email: string;
  readonly picture?: string;
  // Membership
  readonly membershipName?: string;
  readonly membershipStatus?: UserMemberSubscriptionStatus;
  readonly guest: boolean;
  readonly onSite: boolean;
  readonly checkInDate?: string;
  readonly checkOutDate?: string;
  readonly checkInTimeLimit: string;
};

export type VisitTotalReportDTO = {
  readonly userMemberId: string;
  readonly firstName: string;
  readonly lastName: string;
  readonly emailCommunication: boolean;
  readonly membershipName?: string;
  readonly membershipStatus?: UserMemberSubscriptionStatus;
  readonly checkInDate: string;
  readonly checkOutDate?: string;
};

export type NoVisitReportDTO = {
  readonly userMemberId: string;
  readonly firstName: string;
  readonly lastName: string;
  readonly emailCommunication: boolean;
  readonly membershipName?: string;
  readonly membershipStatus?: UserMemberSubscriptionStatus;
};

export type VisitPercentageDTO = {
  readonly visit: number;
  readonly noVisit: number;
};

export type MembershipStatusHistoryItem = {
  id: number;
  status: UserMemberSubscriptionHistoryStatus;
  createdDate: string;
};
export type StaffUserRole = "TRAINER" | "RECEPTION" | "MANAGER" | "OWNER";
export type UserRole = "MEMBER" | StaffUserRole;

export type RevenueType =
  | "transaction-type"
  | "scheduled-revenue"
  | "accounts-receivable";
export type StripeAccountType =
  | "CONNECTED"
  | "NOT_CONNECTED"
  | "STANDARD"
  | "STANDARD_WHILE_ON_BOARDING"
  | "READY_TO_CONNECT";

export type AppointableCategoryDTO = {
  /** @format int64 */
  readonly id: number;
  readonly name: string;
  readonly color: string;
};

export type UserStaffPublicDTO = {
  readonly id: string;
  readonly firstName: string;
  readonly lastName: string;
  readonly picture?: string;
};

export type AppointableDtoAvailabilityType =
  | "STAFF"
  | "FACILITY"
  | "STAFF_AND_FACILITY"
  | "FACILITY_AND_STAFF";

export type AppointableDtoStatus = "ACTIVE" | "INACTIVE";

export type AppointableDtoPricingModel =
  | "FIXED"
  | "FREE"
  | "FROM"
  | "PER_PERSON";

export type AppointableDTO = {
  /** @format int64 */
  readonly id: number;
  readonly name: string;
  readonly description: string;
  readonly termsAndConditions?: string;
  readonly availabilityType: AppointableDtoAvailabilityType;
  readonly status?: AppointableDtoStatus;
  /** @format int32 */
  readonly duration: number;
  /** @format int32 */
  readonly blockedTime?: number;
  readonly alignToInterval?: number;
  /** @format int32 */
  readonly capacity: number;
  readonly pricingModel: AppointableDtoPricingModel;
  readonly price?: number;
  /** @format int32 */
  readonly sessionCost?: number;
  /** @format float */
  readonly taxRate?: number;
  readonly paymentUpfront: boolean;
  readonly cardUpfront: boolean;
  readonly allowOnlineBookings: boolean;
  readonly allowAppBookings: boolean;
  readonly appointableCategory: AppointableCategoryDTO;
  readonly appointableHosts: UserStaffPublicDTO[];
  readonly appointableFacilities: FacilityDTO[];
  readonly forMembersOnly: boolean;
  /** @format int64 */
  readonly clubId: number;
  readonly hasAppointments: boolean;
  readonly membersOnlyPrice?: number;
  readonly slotAlignment: AppointableDtoSlotAlignment;
};

export type AppointableDtoSlotAlignment = "ANYTIME" | "HOUR_START";

export type AppointablePostDTO = {
  readonly name: string;
  readonly description: string;
  readonly termsAndConditions?: string;
  readonly availabilityType: AppointableDtoAvailabilityType;
  readonly forMembersOnly: boolean;
  readonly slotAlignment: AppointableDtoSlotAlignment;
  readonly alignToInterval?: number;
  readonly status: AppointableDtoStatus;
  /** @format int32 */
  readonly duration: number;
  /** @format int32 */
  readonly blockedTime: number;
  /** @format int32 */
  readonly capacity: number;
  readonly pricingModel: AppointableDtoPricingModel;
  readonly price?: number;
  /** @format int32 */
  readonly sessionCost?: number;
  /** @format float */
  readonly taxRate?: number;
  readonly paymentUpfront: boolean;
  readonly cardUpfront: boolean;
  readonly allowOnlineBookings: boolean;
  readonly allowAppBookings: boolean;
  /** @format int64 */
  readonly appointableCategoryId: number;
  readonly appointableHostsIdList: string[];
  readonly appointableFacilitiesIdList: number[];
  /** @format int64 */
  readonly clubId: number;
  readonly membersOnlyPrice?: PriceDTO;
};

export interface AppointablePatchDTO extends Partial<AppointablePostDTO> {
  removePrice?: boolean;
  removeSessionCost?: boolean;
}

export type AppointmentStatus =
  | "BOOKED"
  | "ATTENDED"
  | "CANCELLED"
  | "LATE_CANCELLED"
  | "NO_SHOW";

export type AppointmentPaymentStatus = "PAID" | "UNPAID" | "REFUNDED";

export interface UserMemberPublicDTO {
  readonly id: string;
  readonly email: string;
  readonly phoneNumber?: string;
  readonly firstName: string;
  readonly lastName: string;
  readonly picture?: string;
  readonly profileType: UserProfileType;
  readonly userType: UserType;
}

export interface NoteAppointmentDTO {
  readonly content: string;
  readonly attachment?: string;
  readonly attachmentFilename?: string;
  /** @format date-time */
  readonly createdDate: string;
  /** @format date-time */
  readonly updatedDate?: string;
  readonly createdBy: string;
  readonly createdByFirstName: string;
  readonly createdByLastName: string;
  readonly createdByPicture?: string;
  /** @format int64 */
  readonly appointmentId: number;
  readonly noteId: number;
}

export interface AppointmentDTO {
  /** @format int64 */
  readonly id: number;
  readonly recurringAppointmentId?: number;
  readonly status: AppointmentStatus;
  readonly paymentStatus: AppointmentPaymentStatus;
  /** @format date-time */
  readonly startDate: string;
  /** @format date-time */
  readonly endDate: string;
  readonly appointable: AppointableDTO;
  readonly appointmentUser: UserMemberPublicDTO;
  readonly appointmentHost?: UserStaffPublicDTO;
  readonly appointmentFacility?: FacilityDTO;
  /** @format int32 */
  readonly capacity: number;
  readonly pricingModel: AppointableDtoPricingModel;
  readonly finalPrice?: number;
  /** @format int32 */
  readonly finalSessionCost?: number;
  /** @format float */
  readonly taxRate: number;
  readonly paymentUpfront: boolean;
  readonly cardUpfront: boolean;
  readonly noteList: NoteAppointmentDTO[];
  /** @format int64 */
  readonly clubId: number;
  /*** always set for appointment with kisi */
  readonly kisiGroupId?: string;
  /*** set for appointment with kisi during booking*/
  readonly kisiRoleAssignmentId?: string;
  readonly invoiceNumberAwaitingAuthList?: string[];
}

export interface AppointmentPostDTO {
  readonly status: AppointmentStatus;
  /** @format date-time */
  readonly startDate: string;
  /** @format int64 */
  readonly appointableId: number;
  readonly appointmentUserId: string;
  readonly appointmentHostId?: string;
  readonly appointmentFacilityId?: number;
  readonly availabilityValidationIgnored?: boolean;
  readonly appointmentPayment?:
    | {
        paidWithSessions: true;
      }
    | {
        paidWithSessions: false;
        paymentMethodId?: string;
        promotionCode?: string;
      };
}

export type MemberAppointmentPostDTO = {
  appointmentHostIdUnset?: boolean;
  appointmentFacilityIdUnset?: boolean;
} & Omit<
  AppointmentPostDTO,
  "appointmentUserId" | "availabilityValidationIgnored"
>;

export type MonthlyDayPosition =
  | "FIRST"
  | "SECOND"
  | "THIRD"
  | "FOURTH"
  | "FIFTH"
  | "LAST";

export type RecurringAppointmentType = "DAILY" | "WEEKLY" | "MONTHLY";

export type RecurringAppointmentDTO = {
  /** @format HH:mm:ss */
  readonly appointmentTime: string;
  readonly recurringType: RecurringAppointmentType;
  readonly recurringInterval: number;
  readonly weeklyDays?: DayOfWeek[];
  readonly monthlyDayOfMonth?: number;
  readonly monthlyDayPosition?: MonthlyDayPosition;
  readonly monthlyDayOfWeek?: DayOfWeek;
  readonly appointableId: number;
  /** @format YYYY-MM-dd */
  readonly startDate: string;
  /** @format YYYY-MM-dd */
  readonly endDate?: string;
  readonly id: number;
  readonly lastGeneratedDate: string;

  readonly appointmentUserId: string;
  readonly appointmentUserFirstName: string;
  readonly appointmentUserLastName: string;
} & (
  | {
      readonly appointmentHostId: string;
      readonly appointmentHostFirstName: string;
      readonly appointmentHostLastName: string;
      readonly appointmentHostPicture?: string;
    }
  | {
      readonly appointmentFacilityId: number;
      readonly appointmentFacilityName: string;
      readonly appointmentFacilityPicture?: string;
    }
);

export type RecurringAppointmentPostDTO = {
  /** @format HH:mm */
  readonly appointmentTime: string;
  readonly recurringType: RecurringAppointmentType;
  readonly recurringInterval: number;
  readonly weeklyDays?: DayOfWeek[];
  readonly monthlyDayOfMonth?: number;
  readonly monthlyDayPosition?: MonthlyDayPosition;
  readonly monthlyDayOfWeek?: DayOfWeek;
  readonly appointableId: number;
  readonly appointmentUserId: string;
  readonly appointmentHostId?: string;
  readonly appointmentFacilityId?: number;
  /** @format YYYY-MM-dd */
  readonly startDate: string;
  /** @format YYYY-MM-dd */
  readonly endDate?: string;
};

export type RecurringAppointmentPutDTO = {
  /** @format HH:mm */
  readonly appointmentTime: string;
  readonly recurringType: RecurringAppointmentType;
  readonly recurringInterval: number;
  readonly weeklyDays?: DayOfWeek[];
  readonly monthlyDayOfMonth?: number;
  readonly monthlyDayPosition?: MonthlyDayPosition;
  readonly monthlyDayOfWeek?: DayOfWeek;
  readonly appointableId: number;
  readonly appointmentUserId: string;
  readonly appointmentHostId?: string;
  readonly appointmentFacilityId?: number;
  /** @format YYYY-MM-dd */
  readonly startDate: string;
  /** @format YYYY-MM-dd */
  readonly endDate?: string;
  readonly fromAppointmentId: number;
};

export type AvailabilityDTO = {
  readonly availabilitySlotList: AvailabilitySlot[];
};

export type AvailabilitySlot = {
  readonly availabilityType: "STAFF" | "FACILITY";
  /** @format yyyy-MM-dd */
  readonly startTime: string;
  /** @format yyyy-MM-dd */
  readonly endTime: string | null;
  readonly weekDay: DayOfWeek;
};

export type FacilityAvailabilityDTO = {
  readonly availabilitySlotList: FacilityAvailabilitySlot[];
};

export type FacilityAvailabilitySlot = {
  readonly availabilityType: "FACILITY";
  /** @format HH:mm:ss */
  readonly startTime: string;
  /** @format HH:mm:ss */
  readonly endTime: string;
  readonly weekDay: DayOfWeek;
};

export type HostAvailabilityDTO = {
  readonly availabilitySlotList: HostAvailabilitySlot[];
};

export type HostAvailabilitySlot = {
  readonly availabilityType: "STAFF";
  /** @format HH:mm:ss */
  readonly startTime: string;
  /** @format HH:mm:ss */
  readonly endTime: string;
  readonly weekDay: DayOfWeek;
};

export type AvailabilityWindow = {
  startTime: string;
  endTime: string;
  availableHostsIdList: string[];
  availableFacilityIdList: number[];
};

export type GlobalStatisticsCategories =
  | "revenue"
  | "membership"
  | "event"
  | "lead"
  | "visit";

export type RevenueStatisticField =
  | "total"
  | "membership"
  | "sessionPack"
  | "product"
  | "credits"
  | "writeOff"
  | "refunds"
  | "awaitingAuth";
export type MembershipStatisticField =
  | "active"
  | "paused"
  | "cancelled"
  | "signUps"
  | "overdue"
  | "ending"
  | "growth"
  | "total"
  | "pending"
  | "pausing"
  | "resuming";
export type EventStatisticField =
  | "attendance"
  | "bookingPercentage"
  | "classesRun"
  | "noShows"
  | "lateCancellations";
export type LeadStatisticField =
  | "created"
  | "opened"
  | "total"
  | "closed"
  | "lost"
  | "closeRatio";
export type VisitStatisticField = "total" | "noVisits";

export type GlobalStatisticTrendPeriod = "DAY" | "WEEK" | "MONTH" | "YEAR";

export type ValueStatisticViewDTO = {
  readonly datePeriod?: string;
  readonly amount?: number;
};

export type GlobalStatisticViewDTO = {
  readonly avg?: number;
  readonly var?: number;
  readonly values?: ValueStatisticViewDTO[];
  readonly total?: number;
  readonly lastYearTotal?: number;
};

export type GlobalLeadStatisticDTO = {
  readonly trendPeriod?: GlobalStatisticTrendPeriod;
  readonly total?: GlobalStatisticViewDTO;
  readonly created?: GlobalStatisticViewDTO;
  readonly opened?: GlobalStatisticViewDTO;
  readonly closed?: GlobalStatisticViewDTO;
  readonly lost?: GlobalStatisticViewDTO;
  readonly closeRatio?: GlobalStatisticViewDTO;
};

export type GlobalEventStatisticDTO = {
  readonly trendPeriod?: GlobalStatisticTrendPeriod;
  readonly attendance?: GlobalStatisticViewDTO;
  readonly bookings?: GlobalStatisticViewDTO;
  readonly cancellations?: GlobalStatisticViewDTO;
  readonly bookingPercentage?: GlobalStatisticViewDTO;
  readonly classesRun?: GlobalStatisticViewDTO;
  readonly noShows?: GlobalStatisticViewDTO;
  readonly lateCancellations?: GlobalStatisticViewDTO;
};

export type GlobalMembershipStatisticDTO = {
  readonly trendPeriod?: GlobalStatisticTrendPeriod;
  readonly pending?: GlobalStatisticViewDTO;
  readonly active?: GlobalStatisticViewDTO;
  readonly paused?: GlobalStatisticViewDTO;
  readonly cancelled?: GlobalStatisticViewDTO;
  readonly signUps?: GlobalStatisticViewDTO;
  readonly ending?: GlobalStatisticViewDTO;
  readonly overdue?: GlobalStatisticViewDTO;
  readonly total?: GlobalStatisticViewDTO;
  readonly growth?: GlobalStatisticViewDTO;
  readonly pausing?: GlobalStatisticViewDTO;
  readonly resuming?: GlobalStatisticViewDTO;
};

export type GlobalRevenueStatisticDTO = {
  readonly trendPeriod?: GlobalStatisticTrendPeriod;
  readonly total?: GlobalStatisticViewDTO;
  readonly membership?: GlobalStatisticViewDTO;
  readonly sessionPack?: GlobalStatisticViewDTO;
  readonly product?: GlobalStatisticViewDTO;
  readonly credits?: GlobalStatisticViewDTO;
  readonly writeOff?: GlobalStatisticViewDTO;
  readonly refunds?: GlobalStatisticViewDTO;
  readonly awaitingAuth?: GlobalStatisticViewDTO;
};

export type GlobalVisitRevenueStatisticDTO = {
  readonly trendPeriod: GlobalStatisticTrendPeriod;
  readonly total?: Omit<GlobalStatisticViewDTO, "var">;
  readonly noVisits?: { total: number };
};

export type GlobalStatisticDTO = {
  readonly years?: number[];
  readonly revenue?: GlobalRevenueStatisticDTO;
  readonly membership?: GlobalMembershipStatisticDTO;
  readonly event?: GlobalEventStatisticDTO;
  readonly lead?: GlobalLeadStatisticDTO;
  readonly visit?: GlobalVisitRevenueStatisticDTO;
};

export type AvailabilityOverridePostDTO = {
  readonly isUnavailableAllDay: boolean;
  readonly startTime: string;
  readonly endTime: string;
  /** @format date */
  readonly overrideDate: string;
};

export type AvailabilityOverrideDTO = {
  /** @format int64 */
  readonly id: number;
  readonly isUnavailableAllDay: boolean;
  /** @format HH:mm:ss */
  readonly startTime: string;
  /** @format HH:mm:ss */
  readonly endTime: string;
  /** @format yyyy-MM-dd */
  readonly overrideDate: string;
};

export type AvailabilityCalculationResult = {
  readonly startTime: string;
  readonly endTime: string;
  readonly availableHostsIdList: string[];
};

export type AvailabilityByHostCalculationResponse = Record<
  string,
  AvailabilityCalculationResult[]
>;

export type CheckInDTO = {
  checkInDate: string;
  clubId: number;
  guest: boolean;
  id: number;
};

export type AppointmentPurchaseSummaryPostDTO = {
  readonly appointableId: number;
  readonly userMemberId: string;
  readonly appointmentPaymentPostDTO: {
    readonly paidWithSessions?: boolean;
    readonly appointmentId?: number;
    readonly promotionCode?: string;
    readonly paymentMethodId?: string;
  };
};
export type AppointmentSummaryAsMemberPostDTO = Omit<
  AppointmentPurchaseSummaryPostDTO,
  "userMemberId"
>;
export type SessionPackUsageResult =
  | "UNLIMITED"
  | "USE_SESSIONS"
  | "NOT_ENOUGH_SESSIONS";

export type AppointmentPurchaseSummaryDTO = {
  readonly appointableId: number;
} & (
  | {
      readonly paidWithSessions: false;
      readonly cardUsage: {
        readonly upfrontPriceDetails: {
          readonly totalAmountBeforeDiscount: number;
          readonly totalAmountToPay: number;
          readonly totalAmountDifference: number;
        };
        readonly validPromotionCode: ValidPromotionCode;
      } & (
        | {
            readonly promotionCode: string;
            readonly promotionUpfrontAmount: string;
            readonly promotionUpfrontDiscountType: DiscountType;
          }
        | {}
      );
    }
  | {
      readonly paidWithSessions: true;
      readonly sessionPackUsage: SessionPackUsage;
    }
);
export type SessionPackUsage = {
  readonly sessionPackUsageCalculations: {
    readonly sessionPack: UserMemberSubscriptionBean;
    readonly numberOfSessionsUsed: number;
  }[];
  readonly result: SessionPackUsageResult;
};
// eslint-disable-next-line @typescript-eslint/no-namespace
namespace MemberBooking {
  export type Base = {
    activityId: number;
    startDate: string;
    endDate: string;
    bookingName?: string;
    hostName?: string;
    hostStaffId?: string;
    hostPicture?: string;

    facilityName?: string;
    facilityStaffId?: string;
    facilityPicture?: string;
  };
  export type EventOccurrence = {
    eventOccurrenceId: number;
    eventRsvpStatus?: EventRsvpStatus;
    sessionCost: number;
    capacity: number;
    waitListCapacity: number;
  };

  export type EventOccurrenceAgenda = EventOccurrence & {
    bookedCount: number;
    waitingCount: number;
    eventRsvpId?: number;
    bookingDescription: string;
    bookable?: boolean;
    facilityName: string;
    waitlistQueuePosition?: number;
  };

  export type Appointment = {
    appointmentId: number;
    status?: AppointmentStatus;
    appointmentStatus: AppointmentStatus;
    participantName: string;
    participantPicture?: string;
    paymentStatus: AppointmentPaymentStatus;
    recurringAppointmentId?: number;
  };
}

export type MemberBookingAppointmentDTO = MemberBooking.Base &
  MemberBooking.Appointment;
export type MemberBookingOccurrenceDTO = MemberBooking.Base &
  MemberBooking.EventOccurrence;

export type MemberBookingDTO = MemberBooking.Base &
  (MemberBooking.Appointment | MemberBooking.EventOccurrence);

export type MemberBookingAgendaAppointmentDTO = MemberBooking.Base &
  MemberBooking.Appointment;
export type MemberBookingAgendaOccurrenceDTO = MemberBooking.Base &
  MemberBooking.EventOccurrenceAgenda;
export type MemberBookingAgendaDTO = MemberBooking.Base &
  (MemberBooking.Appointment | MemberBooking.EventOccurrenceAgenda);

export type TokenPageableRequestParams = {
  limit: number;
  nextPageToken?: string;
};

export type UserMemberListItem = {
  userMemberId: string;
  firstName: string;
  lastName: string;
  createdDate: string;
  email: string;
  mobileNumber: string;
  picture?: string;
  membershipName: string;
  membershipStatus: UserMemberSubscriptionStatus;
  membershipPrice: number;
  creditsRemaining: number;
  creditsUnlimited: boolean;
};

export type MembershipCalculateCancelDatesResponse = {
  readonly cancellationDateList: string[];
};

export type InvitationProcessStatusDTO = {
  readonly passwordChanged: boolean;
  readonly paymentMethodCreated: boolean;
};

export type PaymentMethodSetupIntent = {
  readonly setupIntentId: string;
  readonly setupIntentStatus: string;
  readonly setupIntentClientSecret: string;
};

export type CalculateScheduleDates = { readonly changeDates: string[] };

export type SMSPurpose = "MARKETING" | "ACCOUNT_RELATED";

export type SMSPayload = {
  purpose: SMSPurpose;
  body: string;
  schedule?: string;
};

export interface SMSSendingResponse {
  readonly sentSms: number;
  readonly sentAttempts: number;
  readonly failedMobileNumbers: string[];
  readonly failedAttemptsWithNoMobileNumber: [
    { readonly firstName: string; readonly lastName: string },
  ];
}

export interface CreditTopUpItem {
  id: number;
  creditsAmount: number;
  createdDate: string;
}

export interface CreditSpentItem {
  id: number;
  creditsAmount: number;
  createdDate: string;
  messageBody: string;
}

export interface UserMemberAccessLogItem {
  id: string;
  checkInDate: string;
  checkOutDate: string;
}

export interface StripeProductItem {
  id: number;
  creditsAmount: number;
  pricePerCredit: number;
  totalPrice: number;
  currency: Currency;
  url: string;
}

export interface CustomPaymentCategoryDTO {
  id: number;
  name: string;
}

export interface CalculateChangeSubscriptionPriceNewRequestBody {
  subscriptionId: number;
  membershipId: number;
}
export interface CalculateChangeSubscriptionPriceNewResponseBody {
  price: number;
  currentSubscriptionValue?: number;
  newMembershipPrice?: number;
}

export interface ProductOrderCheckoutPaymentMethodRequestBody {
  userMemberId: string;
  orderItems: ProductOrderDetailDTO[];
  orderKey: string;
  paymentMethodId?: string;
  promotionCode?: string;
  priceOverride?: number;
  walletAmount?: number;
}

export interface ProductOrderCheckoutCustomPaymentRequestBody {
  userMemberId: string;
  orderItems: ProductOrderDetailDTO[];
  orderKey: string;
  customPaymentCategoryId: number;
  promotionCode?: string;
  priceOverride?: number;
  walletAmount?: number;
}

export interface ProductOrderInvoiceDTO {
  invoiceNumber: string;
  productOrderId: number;
  invoiceStatus: InvoiceStatus;
}

export interface MembershipSignupPaymentMethodRequestBody {
  userMemberId: string;
  membershipId: number;
  startDate: string;
  orderKey: string;
  paymentMethodId?: string;
  promotionCode?: string;
  priceOverride?: number;

  walletAmount?: number;
}

export interface MembershipSignupCustomPaymentRequestBody {
  userMemberId: string;
  membershipId: number;
  startDate: string;
  orderKey: string;
  customPaymentCategoryId: number;
  promotionCode?: string;
  priceOverride?: number;
  walletAmount?: number;
}

export interface SessionPackSignupPaymentMethodRequestBody {
  userMemberId: string;
  sessionPackId: number;
  startDate: string;
  orderKey: string;
  paymentMethodId?: string;
  promotionCode?: string;
  priceOverride?: number;
  walletAmount?: number;
}

export interface SessionPackSignupCustomPaymentRequestBody {
  userMemberId: string;
  sessionPackId: number;
  startDate: string;
  orderKey: string;
  customPaymentCategoryId: number;
  promotionCode?: string;
  priceOverride?: number;
  walletAmount?: number;
}

export interface SubscriptionInvoiceDTO {
  invoiceNumber: string;
  subscriptionId: number;
  invoiceStatus: InvoiceStatus;
}

export interface ClubShopSettingsDTO {
  readonly voucherSalesEnabled: boolean;
}

export type ClubPropertiesSettings = {
  readonly club: ClubBean;
  readonly properties: ClubPropertiesDTOWithRawPhoneNumberCountry;
  readonly webSettings: ClubWebSettings;
  readonly communicationProperties: ClubCommunicationPropertiesDTO;
  readonly featureFlags: FeatureFlagType;
  readonly appSettings: ClubAppSettingsDTO;
  readonly shopSettings: ClubShopSettingsDTO;
};

export type ClubAppSettingsDTO = {
  checkInWidgetEnabled: boolean;
  buyMembershipWidgetEnabled: boolean;
  buyMembershipWidgetPosition: number;
  buyPackageWidgetEnabled: boolean;
  buyPackageWidgetPosition: number;
  gymCapacityWidgetEnabled: boolean;
  gymCapacityWidgetPosition: number;
  appointmentBookingWidgetEnabled: boolean;
  appointmentBookingWidgetPosition: number;
  classBookingWidgetEnabled: boolean;
  classBookingWidgetPosition: number;
  classAttendanceWidgetEnabled: boolean;
  classAttendanceWidgetPosition: number;
  appointmentAttendanceWidgetEnabled: boolean;
  appointmentAttendanceWidgetPosition: number;
  gymAttendanceWidgetEnabled: boolean;
  gymAttendanceWidgetPosition: number;
  newsFeedWidgetEnabled: boolean;
  newsFeedWidgetPosition: number;
  classesNavigationEnabled: boolean;
  appointmentsNavigationEnabled: boolean;
  shopNavigationEnabled: boolean;
  classesCapacityShowMode: ClassCapacityMode;
};

export type ClubClassesSettingsDTO = {
  classesCapacityShowMode: ClassCapacityMode;
};

export type ClassCapacityMode = "DYNAMIC" | "ALWAYS_SHOW" | "NEVER_SHOW";

export type BrandingPatchDTO = Partial<{
  primaryColor: string;
  font: string;
  darkMode: boolean;
  membershipsNavEnabled: boolean;
  creditPacksNavEnabled: boolean;
  appointmentsNavEnabled: boolean;
  timetableNavEnabled: boolean;
  leadCaptureNavEnabled: boolean;
  yourWebsiteUrl: string;
  redirectUrl: string;
  grayShades: { [key: string]: string };
}>;

export type NotePostDTO = {
  content: string;
} & ({ appointmentId: number } | { userMemberId: string } | { leadId: number });

export type AppointmentBookingPaymentByPaymentMethodIdProps = {
  appointmentId: number;
  paymentMethodId: string;
  promotionCode?: string;
};

export type AppointmentBookingCustomPaymentByCustomPaymentCategoryIdProps = {
  appointmentId: number;
  customPaymentCategoryId: number;
  promotionCode?: string;
};

export type AppointmentBookingConsumeSessionsProps = {
  appointmentId: number;
  promotionCode?: string;
};

export type AppointmentInvoiceDTO = {
  invoiceNumber: string;
  invoiceStatus: InvoiceStatus;
};

export type SubscriptionSessionPackDTO = {
  id: number;
  name: string;
  status: UserMemberSubscriptionStatus;
  sessionsLeft?: number;
  sessionsIncluded: number;
  expiryDate?: string;
  termsConditions: string;
  sessionsUnlimited: boolean;
  partOfMembership: boolean;
  membershipName?: string;
};

export type SubscriptionSessionPackUsageHistoryDTOStatus =
  | AppointmentStatus
  | EventRsvpStatus;

export type SubscriptionSessionPackUsageHistoryDTO = {
  name: string;
  startDate: string;
  status: SubscriptionSessionPackUsageHistoryDTOStatus;
  sessionsUsed: number;
  usedAt: string;
};

export type CurrentSubscriptionStatus =
  | "ACTIVE"
  | "PENDING"
  | "ACTIVE_CHANGING"
  | "ACTIVE_PAUSING"
  | "ACTIVE_CANCELLING"
  | "PAUSED"
  | "OVERDUE_AUTH_NEEDED"
  | "OVERDUE_PAYMENT";

export type CurrentSubscriptionType = "PREPAID" | "RECURRING" | "TRIAL";

export type CurrentSubscriptionDTO = {
  id: number;
  price: number;
  name: string;
  type: CurrentSubscriptionType;
  status: CurrentSubscriptionStatus;
  billingType?: BillingType;
  billingPeriod: number;
  monthlyBillingDay?: number;
  weeklyBillingDay?: DayOfWeek;
  durationType: MembershipDurationType;
  pendingStartDate?: string;
  endContractDate?: string;
  termsConditionsUrl: string;
  scheduledChangeDate?: string;
  scheduledChangeToMembershipName?: string;
  pauseStartDate?: string;
  pauseEndDate?: string;
  cancellationDate?: string;
  changeMembershipAvailable: boolean;
  cancelMembershipAvailable: boolean;
  invoiceNumber?: string;
  isPrimary: boolean;
  hasSecondaryMembers: boolean;
  fixedStartDate?: string;
  scheduledInvoiceIssueDate?: string;
};

export type MembershipForChangeDTO = {
  id: number;
  name: string;
  defaultPrice: number;
  billingType?: BillingType;
  billingPeriod: number;
};

export type SubscriptionMembershipChangeDatesDTO = {
  changeDates: string[];
};

export type SubscriptionMembershipChangeSchedulePostDTO = {
  membershipId: number;
  changeDate: string;
};

export type CancellationDatesDTO = { cancellationDateList: string[] };

export type SubscriptionPendingStartDateDTO = {
  pendingStartDate?: string;
  startSubscriptionEffectiveImmediately?: boolean;
};

export type CancellationReasonDTO = {
  id: number;
  name: string;
};

export type SubscriptionMembershipPreviousDTO = {
  id: number;
  price: number;
  name: string;
  type: CurrentSubscriptionType;
  billingType?: BillingType;
  billingPeriod: number;
  monthlyBillingDay?: number;
  weeklyBillingDay?: DayOfWeek;
  startDate: string;
  endDate: string;
  endContractDate?: string;
  termsConditionsUrl: string;
};

export type EventRsvpCustomerDTO = {
  id: number;
  startDate: string;
  endDate: string;
  status: EventRsvpStatus;
  activityName: string;
  hostFirstName: string;
  hostLastName: string;
  hostPicture?: string;
  facilityName: string;
  facilityPicture?: string;
  waitlistPosition?: number;
};

export type AppointmentCustomerDTO = {
  id: number;
  startDate: string;
  endDate: string;
  status: AppointmentStatus;
  appointableName: string;
  hostFirstName?: string;
  hostLastName?: string;
  hostPicture?: string;
  facilityName?: string;
  facilityPicture?: string;
};

export type XeroStatus =
  | "DISABLED"
  | "ONBOARDING"
  | "ENABLED"
  | "NEED_AUTHORIZATION";

export type XeroTenantStatusDTO = {
  status: XeroStatus;
};

export type XeroAuthPageDTO = {
  authPageRedirectUrl: string;
};

export type XeroAccountMappingOptionDTO = {
  id: string;
  name: string;
};

export type XeroAccountMappingDTO = {
  clearing: XeroAccountMappingOptionDTO;
  payout?: XeroAccountMappingOptionDTO;
  charge?: XeroAccountMappingOptionDTO;
  refund?: XeroAccountMappingOptionDTO;
  fee?: XeroAccountMappingOptionDTO;
  adjustment?: XeroAccountMappingOptionDTO;
  other?: XeroAccountMappingOptionDTO;
};

export type XeroAccountMappingPatchDTO = {
  payoutAccountId: string;
  chargeAccountId: string;
  refundAccountId: string;
  feeAccountId: string;
  adjustmentAccountId: string;
  otherAccountId: string;
};

export type XeroAccountMappingOptionListDTO = {
  payout: XeroAccountMappingOptionDTO[];
  charge: XeroAccountMappingOptionDTO[];
  refund: XeroAccountMappingOptionDTO[];
  fee: XeroAccountMappingOptionDTO[];
  adjustment: XeroAccountMappingOptionDTO[];
  other: XeroAccountMappingOptionDTO[];
};

export type ZapierStatus = "ENABLED" | "DISABLED";

export type ClubZapierStatusDTO = {
  status: ZapierStatus;
};

export type ClubZapierConfigDTO = {
  clubId: number;
  zapierApiKey: string;
};

export type ClubCommunicationPropertiesDTO = {
  defaultEmailCommunication: boolean;
  defaultSmsCommunication: boolean;
};

export type AppointableCategoryPatchDTO = {
  name?: string;
  color?: string;
};

export type FeatureFlags = {
  readonly featureFlags: FeatureFlagType;
};
export interface ApiMeta {
  apiUrl: string;
  keycloakRealm: string;
  keycloakUrl: string;
  appId?: string;
}

export type JWT = {
  exp: number;
  iat: number;
  jti: string;
  iss: string;
  sub: string;
  typ: string;
  azp: string;
  session_state: string;
  acr: string;
  "allowed-origins": string[];
  realm_access: {
    roles: UserRole[];
  };
  scope: string;
  sid: string;
  email_verified: boolean;
  preferred_username: string;
  email: string;
};

export interface TokenStorage {
  set(key: string, value: string): Promise<void>;
  get(key: string): Promise<string | null>;
  remove(key: string): Promise<void>;
  log(): Promise<void>;
}

export type PropertiesSettingsDTO = {
  club: ClubBean;
  properties: ClubPropertiesDTO;
  webSettings: ClubWebSettings;
  communicationProperties: ClubCommunicationPropertiesDTO;
  featureFlags: FeatureFlagType;
  appSettings: ClubAppSettingsDTO;
  shopSettings: ClubShopSettingsDTO;
};

export type CheckInPassDTO = {
  membershipName?: string;
  bookingName?: string;
  bookingStartDate?: string;
};

export type ClubOnboardingSettingStatus = "REQUIRED" | "OPTIONAL" | "DISABLED";

export type SmsSettingsDTO = {
  smsEnabled: boolean;
  twilioAccountSid: string;
  twilioMobileNumber: string;
  messagingCreditsLeft: number;
  lastCreditsTopUpAt: string;
};

export type ClubOnboardingSettingsDTO = {
  mobileNumberSetting: ClubOnboardingSettingStatus;
  dateBirthSetting: ClubOnboardingSettingStatus;
  addressSetting: ClubOnboardingSettingStatus;
  postCodeSetting: ClubOnboardingSettingStatus;
  citySetting: ClubOnboardingSettingStatus;
  genderSetting: ClubOnboardingSettingStatus;
  emergencyContactSetting: ClubOnboardingSettingStatus;
  personalNumberSetting: ClubOnboardingSettingStatus;
  pictureSetting: ClubOnboardingSettingStatus;
};

export type UserMemberCommunicationPatchDTO = {
  emailCommunication?: boolean;
  smsCommunication?: boolean;
  pushCommunication?: boolean;
};

export type StaffOrMember = "staff" | "member";

export type UserMemberSignupDTO = {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  mobileNumber: string;
};
export type UserMemberSignupAsStaffDTO = Omit<
  UserMemberSignupDTO,
  "password"
> & {
  mobileNumber?: string;
};
export type UserStaffProfileDTO = {
  activeUser: boolean;
  email: string;
  firstName: string;
  lastName: string;
  mobileNumber: string;
  picture: string;
  role: string;
  userStaffId: string;
};

export type UserMemberProfileDTO = {
  userMemberId: string;
  email: string;
  firstName: string;
  lastName: string;
  onboardStatus: "INCOMPLETE" | "COMPLETE";
  picture?: string;
  clubWaiverAccepted: boolean;
  clubWaiverAcceptedAt: string;
  customInvoiceField: string;
  mobileNumber: string;
  dateBirth: string;
  addressLine1: string;
  addressLine2: string;
  postCode: string;
  city: string;
  gender: Gender;
  emergencyContactName: string;
  emergencyContact: string;
  personalNumber: string;

  emailCommunication: boolean;
  smsCommunication: boolean;
  pushCommunication: boolean;
};

export type UserMemberProfileDTOPatch = {
  mobileNumber: string;
  dateBirth: string;
  addressLine1: string;
  addressLine2: string;
  postCode: string;
  city: string;
  gender: Gender;
  emergencyContactName: string;
  emergencyContact: string;
  personalNumber: string;
  acceptClubWaiver: boolean;
};

export type AppSettingsNavigationDTO = {
  classesNavigationEnabled: boolean;
  appointmentsNavigationEnabled: boolean;
  shopNavigationEnabled: boolean;
};

export type AppSettingsHomePageDTO = {
  checkInWidgetEnabled: boolean;
  buyMembershipWidgetEnabled: boolean;
  buyMembershipWidgetPosition: number;
  buyPackageWidgetEnabled: boolean;
  buyPackageWidgetPosition: number;
  gymCapacityWidgetEnabled: boolean;
  gymCapacityWidgetPosition: number;
  appointmentBookingWidgetEnabled: boolean;
  appointmentBookingWidgetPosition: number;
  classBookingWidgetEnabled: boolean;
  classBookingWidgetPosition: number;
  classAttendanceWidgetEnabled: boolean;
  classAttendanceWidgetPosition: number;
  gymAttendanceWidgetEnabled: boolean;
  gymAttendanceWidgetPosition: number;
  appointmentAttendanceWidgetEnabled: boolean;
  appointmentAttendanceWidgetPosition: number;
  newsFeedWidgetEnabled: boolean;
  newsFeedWidgetPosition: number;
};

export type PriceDTO = {
  price?: number | null;
};
export type KisiBrandConfig = {
  apiKeyExists: boolean;
  secretKeyExists: boolean;
  webhookUrl: string;
};

export type EventRsvpFeeInvoiceDTO = {
  invoiceNumber: string;
  invoiceStatus: InvoiceStatus;
};

export type UserMemberProfileBadgesDTO = {
  visitCount: number;
  classCount?: number;
  appointmentCount?: number;
};

export type VoucherStatus =
  | "PENDING_PAYMENT"
  | "AVAILABLE"
  | "REDEEMED"
  | "REFUNDED"
  | "VOID";

export type VoucherDTO = {
  id: number;
  amount: number;
  status: VoucherStatus;
  purchasedAt: string;
  purchasedById: string;
  purchasedByFirstName: string;
  purchasedByLastName: string;
  soldById: string;
  soldByFirstName: string;
  soldByLastName: string;
  redeemedAt?: string;
  redeemedById?: string;
  redeemedByFirstName?: string;
  redeemedByLastName?: string;
};

export type WalletBalanceDTO = {
  walletBalance: number;
  lastActivityAt: string;
};

type WalletTransactionType =
  | "CREDIT_TOP_UP"
  | "CREDIT_REDEEM_VOUCHER"
  | "DEBIT_PURCHASE"
  | "DEBIT_TOP_UP_REFUND"
  | "CREDIT_PURCHASE_REFUND";

export type WalletBalanceHistoryDTO = {
  type: WalletTransactionType;
  invoiceNumber?: string;
  voucherCode?: string;
  amount: number;
  createdAt: string;
  createdById?: string;
  createdByFirstName?: string;
  createdByLastName?: string;
};

export type Prettify<T> = {
  [K in keyof T]: T[K];
};
export * from "./api-helper";
export * from "./api-resolver";
